import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { EyeIcon, PercentIcon } from "lucide-react";

import { formatDate, parseDate } from "simplydo/core/dates";
import { getSelectableDeadlineFields } from "simplydo/core/ideas";
import type { OpenAPI } from "simplydo/interfaces";
import { RichText } from "simplydo/react/richtext";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { DateTimeInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import type { InputOnChangeData } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { List, ListItem } from "simplydo/react/ui/list";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import {
  EmailPreviewModal,
  useEmailPreview,
} from "components/lib/EmailPreviewModal";

export type IdeaStatusCatalogueItem = NonNullable<
  OpenAPI.Schemas["Organisation"]["ideaStatuses"]
>[number];

type IdeaAuthorUpdateIdea = {
  _id: string;
  name?: string;
  ideaStatus?: OpenAPI.Schemas["Idea"]["ideaStatus"];
  ownerStatus?: { _id?: string; name?: string };
  status?: string;
  statusMessage?: string;
  submissionDeadline?: OpenAPI.Schemas["Idea"]["submissionDeadline"];
};

type ManageIdeaStatusModalProps = {
  open: boolean;
  ideaId: string;
  ideaIds: string[];
  /** Preselected catalogue status; for Other update, pass current status. */
  status: IdeaStatusCatalogueItem | null;
  statuses: IdeaStatusCatalogueItem[];
  onClose: () => void;
  onUpdated?: (
    ideas: OpenAPI.POST<"/ideas/author-updates">["response"]["ideas"],
  ) => void;
  challengeId?: string;
  ideaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
  /** Idea snapshot(s) for names, message, deadline, and sent-feedback indicators. */
  ideas?: IdeaAuthorUpdateIdea[];
  /** @deprecated Prefer `ideas` — kept for single-idea callers. */
  idea?: IdeaAuthorUpdateIdea | null;
};

export function ManageIdeaStatusModal({
  open,
  ideaId,
  ideaIds,
  status,
  statuses,
  onClose,
  onUpdated,
  challengeId,
  ideaTemplate,
  ideas: ideasProp,
  idea = null,
}: ManageIdeaStatusModalProps) {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const ideas = useMemo(() => {
    if (ideasProp?.length) {
      return ideasProp;
    }
    return idea ? [idea] : [];
  }, [ideasProp, idea]);
  const ideaSnapshot = ideas.length === 1 ? (ideas[0] ?? null) : null;
  const [loading, setLoading] = useState(false);
  const [selectedStatusId, setSelectedStatusId] = useState<string | null>(
    status?._id ?? null,
  );
  const [message, setMessage] = useState("");
  const [notifyAuthors, setNotifyAuthors] = useState(true);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [editableFields, setEditableFields] = useState<string[]>([]);
  const [shouldUnsubmit, setShouldUnsubmit] = useState(true);
  const [assessmentRoundId, setAssessmentRoundId] = useState<string | null>(
    null,
  );
  const [compilingFeedbackRoundId, setCompilingFeedbackRoundId] = useState<
    string | null
  >(null);
  const emailPreview = useEmailPreview();
  const { closePreview } = emailPreview;

  const isBulk = ideaIds.length > 1;
  const previewIdeaId = ideaIds[0] ?? ideaId;
  const selectedStatus =
    statuses.find((s) => s._id === selectedStatusId) ?? status;

  const assessmentRounds = ideaTemplate?.assessments?.rounds || [];
  const assessmentFeedbackSent =
    ideaSnapshot?.ideaStatus?.assessmentFeedbackSent || {};

  const selectableFields = useMemo(
    () => getSelectableDeadlineFields(ideaTemplate, t),
    [ideaTemplate, t],
  );
  const fieldOptions = useMemo(
    () =>
      selectableFields.map((field) => ({
        key: field.id,
        value: field.id,
        text: field.sectionName
          ? `${field.sectionName}: ${field.title}`
          : field.title,
      })),
    [selectableFields],
  );

  const canEditDeadlines =
    !!challengeId &&
    (util.hasPermission(user, "org.manageChallenges", user?.organisation) ||
      util.hasPermission(user, "challenge.editIdeaDeadlines", challengeId));
  const canEditAssessments =
    !isBulk &&
    !!challengeId &&
    assessmentRounds.length > 0 &&
    (util.hasPermission(user, "org.manageChallenges", user?.organisation) ||
      util.hasPermission(user, "challenge.editAssessments", challengeId));

  useEffect(() => {
    if (!open) {
      return;
    }
    const currentMessage =
      ideaSnapshot?.ideaStatus?.message ?? ideaSnapshot?.statusMessage ?? "";
    const currentStatusId =
      status?._id ??
      ideaSnapshot?.ideaStatus?.status ??
      ideaSnapshot?.ownerStatus?._id ??
      ideaSnapshot?.status ??
      null;
    setSelectedStatusId(currentStatusId);
    const catalogue = statuses.find((s) => s._id === currentStatusId) ?? status;
    const sameAsCurrent =
      currentStatusId &&
      (ideaSnapshot?.ideaStatus?.status ?? ideaSnapshot?.status) ===
        currentStatusId;
    setMessage(
      sameAsCurrent && currentMessage
        ? currentMessage
        : (catalogue?.defaultMessage ?? ""),
    );
    setNotifyAuthors(true);
    const existingDeadline = !isBulk
      ? ideaSnapshot?.submissionDeadline
      : undefined;
    if (existingDeadline?.deadline) {
      setDeadline(new Date(existingDeadline.deadline));
      setEditableFields(
        (existingDeadline.editableFields || []).filter(
          (id): id is string => typeof id === "string",
        ),
      );
      setShouldUnsubmit(false);
    } else {
      setDeadline(null);
      setEditableFields([]);
      setShouldUnsubmit(true);
    }
    setAssessmentRoundId(null);
    setCompilingFeedbackRoundId(null);
    closePreview();
  }, [open, status, statuses, ideaSnapshot, isBulk, closePreview]);

  const handleStatusChange = useCallback(
    (nextId: string) => {
      setSelectedStatusId(nextId);
      const catalogue = statuses.find((s) => s._id === nextId);
      const currentId =
        ideaSnapshot?.ideaStatus?.status ??
        ideaSnapshot?.ownerStatus?._id ??
        ideaSnapshot?.status;
      if (
        nextId === currentId &&
        (ideaSnapshot?.ideaStatus?.message || ideaSnapshot?.statusMessage)
      ) {
        setMessage(
          ideaSnapshot?.ideaStatus?.message ??
            ideaSnapshot?.statusMessage ??
            "",
        );
      } else {
        setMessage(catalogue?.defaultMessage ?? "");
      }
    },
    [statuses, ideaSnapshot],
  );

  const buildPayload = useCallback(() => {
    if (!selectedStatusId) {
      return null;
    }
    const payload: OpenAPI.POST<"/ideas/author-updates">["requestBody"] = {
      ideaIds,
      status: selectedStatusId,
      message: message.trim(),
      notifyAuthors,
    };
    if (canEditDeadlines && deadline) {
      const existing = !isBulk ? ideaSnapshot?.submissionDeadline : undefined;
      const existingTime = existing?.deadline
        ? new Date(existing.deadline).getTime()
        : null;
      const existingFields = [...(existing?.editableFields || [])]
        .filter((id): id is string => typeof id === "string")
        .sort();
      const nextFields = [...editableFields].sort();
      const deadlineChanged =
        existingTime == null ||
        deadline.getTime() !== existingTime ||
        JSON.stringify(nextFields) !== JSON.stringify(existingFields) ||
        shouldUnsubmit;
      if (deadlineChanged) {
        payload.submissionDeadline = {
          deadline: new Date(deadline).toISOString(),
          editableFields,
          shouldUnsubmit,
        };
      }
    }
    if (assessmentRoundId && !isBulk) {
      payload.assessmentFeedback = { assessmentRoundId };
    }
    return payload;
  }, [
    selectedStatusId,
    ideaIds,
    message,
    notifyAuthors,
    canEditDeadlines,
    deadline,
    editableFields,
    shouldUnsubmit,
    assessmentRoundId,
    isBulk,
    ideaSnapshot,
  ]);

  const handleConfirm = useCallback(() => {
    const payload = buildPayload();
    if (!payload) {
      toast.error(t("ideas.status.assignModal.statusRequired"));
      return;
    }
    setLoading(true);
    api.ideas.applyAuthorUpdates(
      payload,
      (data) => {
        setLoading(false);
        onUpdated?.(data.ideas);
        onClose();
      },
      (err) => {
        setLoading(false);
        toast.error(err.message);
      },
    );
  }, [buildPayload, onUpdated, onClose, t]);

  const handlePreviewEmail = useCallback(() => {
    if (!selectedStatusId) {
      return;
    }
    emailPreview.loadPreview((success, fail) => {
      const previewBody: OpenAPI.POST<"/ideas/author-updates/preview/{idea_id}">["requestBody"] =
        { status: selectedStatusId, message: message.trim() };
      if (canEditDeadlines && deadline) {
        const existing = !isBulk ? ideaSnapshot?.submissionDeadline : undefined;
        const existingTime = existing?.deadline
          ? new Date(existing.deadline).getTime()
          : null;
        if (existingTime == null || deadline.getTime() !== existingTime) {
          previewBody.submissionDeadline = {
            deadline: new Date(deadline).toISOString(),
            shouldUnsubmit,
          };
        }
      }
      api.ideas.previewAuthorUpdate(
        previewIdeaId,
        previewBody,
        ({ html }) => success(html ?? ""),
        fail,
      );
    });
  }, [
    selectedStatusId,
    emailPreview,
    message,
    canEditDeadlines,
    deadline,
    shouldUnsubmit,
    previewIdeaId,
    isBulk,
    ideaSnapshot,
  ]);

  const insertAssessmentFeedback = useCallback(
    (roundId: string) => {
      if (!ideaId) {
        return;
      }
      setCompilingFeedbackRoundId(roundId);
      api.ideas.previewAssessmentFeedback(
        ideaId,
        { assessmentRoundId: roundId },
        ({ feedbackHtml }) => {
          setCompilingFeedbackRoundId(null);
          const html = feedbackHtml || "";
          setMessage((prev) => {
            const trimmed = prev.trim();
            if (!trimmed) {
              return html;
            }
            return `${trimmed}<p></p>${html}`;
          });
          setAssessmentRoundId(roundId);
          toast.success(t("ideas.authorUpdate.feedbackInserted"));
        },
        (err) => {
          setCompilingFeedbackRoundId(null);
          toast.error(err.message);
        },
      );
    },
    [ideaId, t],
  );

  const lastNotifiedAt = ideaSnapshot?.ideaStatus?.lastNotifiedAt;
  const currentIdeaStatusId = !isBulk
    ? (ideaSnapshot?.ideaStatus?.status ??
      ideaSnapshot?.ownerStatus?._id ??
      ideaSnapshot?.status ??
      null)
    : null;

  if (!open) {
    return null;
  }

  return (
    <>
      <Modal open={open} onClose={onClose} size="small">
        <ModalHeader>
          {selectedStatus
            ? t("ideas.status.assignModal.title", {
                status: selectedStatus.name,
              })
            : t("ideas.authorUpdate.title")}
        </ModalHeader>
        <ModalContent scrolling>
          {isBulk && ideas.length > 0 ? (
            <div>
              <H5>
                {t("ideas.authorUpdate.updatingIdeas")}{" "}
                {util.pluralise(
                  ideas.length,
                  t("generic.idea"),
                  t("generic.ideas"),
                  true,
                )}
                <Subheader>
                  <List variant="bulleted" compact>
                    {ideas.map((item) => (
                      <ListItem key={item._id}>
                        {item.name || item._id}
                      </ListItem>
                    ))}
                  </List>
                </Subheader>
              </H5>
            </div>
          ) : null}

          {lastNotifiedAt ? (
            <Message>
              {t("ideas.authorUpdate.lastNotified", {
                date: formatDate(lastNotifiedAt),
              })}
            </Message>
          ) : null}

          <FormField>
            <FormLabel>{t("ideas.status.manage.fieldLabel")}</FormLabel>
            <p>{t("ideas.status.assignModal.visibilityInfo")}</p>
            <Dropdown
              fluid
              variant="outline"
              placeholder={t("ideas.status.manage.selectPlaceholder")}
              value={selectedStatusId ?? undefined}
              options={statuses.map((s) => ({
                key: s._id,
                value: s._id,
                text: (
                  <Label size="small" color={s.color}>
                    {s.name}
                  </Label>
                ),
                description:
                  currentIdeaStatusId === s._id
                    ? t("ideas.authorUpdate.currentStatus")
                    : undefined,
              }))}
              onChange={(_e, { value }) => {
                if (typeof value === "string") {
                  handleStatusChange(value);
                }
              }}
            />
          </FormField>

          <FormField>
            <FormLabel>{t("ideas.status.assignModal.messageLabel")}</FormLabel>
            <p>{t("ideas.status.assignModal.messageHelp")}</p>
            <Message>{t("ideas.status.assignModal.messageEmailNote")}</Message>
            <RichText
              forType="idea"
              forId={ideaId}
              value={message}
              preset="simple"
              onChange={(html) => setMessage(html)}
              placeholder={t("ideas.status.assignModal.messagePlaceholder")}
            />
            {canEditAssessments ? (
              <div style={{ marginTop: 8 }}>
                {assessmentRounds.length === 1 ? (
                  <Button
                    size="small"
                    variant="outline"
                    loading={!!compilingFeedbackRoundId}
                    disabled={!!compilingFeedbackRoundId}
                    onClick={() =>
                      insertAssessmentFeedback(assessmentRounds[0]!.id)
                    }
                  >
                    <Icon icon={PercentIcon} />
                    {t("ideas.authorUpdate.addAssessmentFeedback")}
                  </Button>
                ) : (
                  <Dropdown
                    trigger={
                      <Button
                        size="small"
                        variant="outline"
                        loading={!!compilingFeedbackRoundId}
                        disabled={!!compilingFeedbackRoundId}
                      >
                        <Icon icon={PercentIcon} />
                        {t("ideas.authorUpdate.addAssessmentFeedback")}
                      </Button>
                    }
                  >
                    <DropdownMenu>
                      {assessmentRounds.map(
                        (round: { id: string; name?: string }) => {
                          const sentAt =
                            assessmentFeedbackSent?.[round.id]?.sentAt;
                          return (
                            <DropdownItem
                              key={round.id}
                              onClick={() => insertAssessmentFeedback(round.id)}
                              description={
                                sentAt ? "Previously sent" : undefined
                              }
                            >
                              {round.name}
                            </DropdownItem>
                          );
                        },
                      )}
                    </DropdownMenu>
                  </Dropdown>
                )}
              </div>
            ) : null}
          </FormField>

          {canEditDeadlines ? (
            <>
              <FormField>
                <FormLabel>{t("ideas.authorUpdate.deadlineSection")}</FormLabel>
                <DateTimeInput
                  clearable
                  value={deadline ?? ""}
                  onChange={(
                    _e: React.SyntheticEvent,
                    { value }: InputOnChangeData,
                  ) => {
                    if (!value) {
                      setDeadline(null);
                      return;
                    }
                    const next = parseDate(value, "dd/MM/yyyy HH:mm");
                    if (next) setDeadline(next);
                  }}
                />
              </FormField>
              {deadline ? (
                <>
                  {fieldOptions.length > 0 ? (
                    <FormField>
                      <Dropdown
                        fluid
                        multiple
                        variant="outline"
                        search
                        clearable
                        placeholder={t(
                          "ideas.deadline.modal.editableFields.placeholder",
                        )}
                        options={fieldOptions}
                        value={editableFields}
                        onChange={(_e, { value }) =>
                          setEditableFields(
                            Array.isArray(value)
                              ? value.filter(
                                  (v): v is string => typeof v === "string",
                                )
                              : [],
                          )
                        }
                      />
                    </FormField>
                  ) : null}
                  <Checkbox
                    toggle
                    checked={shouldUnsubmit}
                    onChange={(_e, d) => setShouldUnsubmit(!!d.checked)}
                  >
                    <CheckboxHeader>
                      {t("ideas.authorUpdate.unsubmitIdeas")}
                    </CheckboxHeader>
                    <CheckboxContent>
                      {t("ideas.authorUpdate.unsubmitIdeasHelp")}
                    </CheckboxContent>
                  </Checkbox>
                </>
              ) : null}
            </>
          ) : null}

          <Divider />

          <Checkbox
            toggle
            checked={notifyAuthors}
            onChange={(_e, { checked }) => setNotifyAuthors(Boolean(checked))}
          >
            <CheckboxHeader>
              {t("ideas.status.assignModal.notifyAuthors")}
            </CheckboxHeader>
            <CheckboxContent>
              {t("ideas.status.assignModal.notifyAuthorsHelp")}
            </CheckboxContent>
          </Checkbox>
        </ModalContent>
        <ModalActions>
          <Button onClick={onClose} disabled={loading}>
            {t("generic.cancel")}
          </Button>
          <Button
            onClick={handlePreviewEmail}
            disabled={loading || !notifyAuthors || !selectedStatusId}
            loading={emailPreview.loading}
          >
            <Icon icon={EyeIcon} />
            {t("emailPreview.previewButton")}
          </Button>
          <Button
            color="primary"
            onClick={handleConfirm}
            loading={loading}
            disabled={!selectedStatusId}
          >
            {t("ideas.authorUpdate.confirm")}
          </Button>
        </ModalActions>
      </Modal>
      <EmailPreviewModal
        open={emailPreview.open}
        onClose={emailPreview.closePreview}
        html={emailPreview.html ?? ""}
      />
    </>
  );
}

/** @deprecated Alias kept for existing imports */
export { ManageIdeaStatusModal as ManageIdeaAuthorUpdateModal };
